import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Hotel, CheckCircle2, AlertTriangle, RefreshCw, Sparkles, Zap, SkipForward } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import EmptyPoolCTA from "@/components/pms/EmptyPoolCTA";
import HotelConnectionChart from "@/components/pms/HotelConnectionChart";

const HOTEL_TYPE_KEYWORDS = ["hotel", "hostel", "dormitory", "dorm", "motel"];

/**
 * Hotel-tier Step 2 — one-click auto-map.
 *
 * We only surface OTA listings whose `type` matches a hotel-tier keyword
 * (Hotel · Hostel · Dormitory · Motel). Tapping one listing does the whole
 * 1:1 inventory-to-unit mapping via `POST /pms/properties/{pid}/mappings/bulk-auto`
 * and advances to billing.
 */
export default function HotelMapping({ data, reload, onDone }) {
  const { property, units, mappings, pool } = data;
  const [busy, setBusy] = useState(null); // holds listing_id being processed
  const [refreshing, setRefreshing] = useState(false);
  // Step 2.5 — connection chart. Auto-opens once a listing is selected (mapped
  // or reserved) and gates the "Continue to billing" click.
  const [showChart, setShowChart] = useState(false);

  // Pending reservation (set by /mappings/bulk-auto but NOT yet locked to
  // the OTA pool — it becomes permanent only on payment success).
  const pendingListingId = property.pending_hotel_listing_id || null;
  const mappedListingId = mappings?.[0]?.listing_id || null;
  const selectedListingId = mappedListingId || pendingListingId;
  const isPaid = property.status === "active";

  const hotelListings = useMemo(() => {
    // Hotel-only pool: keep everything hotel-shaped (by type keyword or by
    // inventory>=2). Also keep whatever is *already selected* for this property
    // (mapped or pending) even if the pool row is missing keywords, so the
    // user can always see the current selection.
    return (pool || []).filter((p) => {
      const isSelected = p.listing_id === selectedListingId;
      if (isSelected) return true;
      if (p.status !== "available") return false;
      const t = (p.type || "").toLowerCase();
      const isHotelType = HOTEL_TYPE_KEYWORDS.some((k) => t.includes(k));
      const multiInv = Number(p.total_inventory || 0) >= 2;
      return isHotelType || multiInv;
    });
  }, [pool, selectedListingId]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/pms/refresh-pool");
      if (r.added > 0) toast.success(`Pulled ${r.added} listing${r.added > 1 ? "s" : ""} from Atiti OTA`);
      else toast.info("Pool is up to date");
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setRefreshing(false);
  };

  const pickListing = async (listing) => {
    setBusy(listing.listing_id);
    try {
      const { data: r } = await api.post(`/pms/properties/${property.id}/mappings/bulk-auto`, {
        listing_id: listing.listing_id,
      });
      const invTxt = r.inventory ? ` · ${r.pair_count}/${r.unit_count} unit${r.unit_count === 1 ? "" : "s"} linked` : "";
      if (r.pending) {
        toast.success(`Reserved · "${listing.title}"${invTxt} · locks after payment`);
      } else {
        toast.success(`Mapped · ${r.mapped} units linked to "${listing.title}"`);
      }
      await reload();
      setShowChart(true);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };

  const goToBilling = async (skip = false) => {
    setBusy("finish");
    try {
      await api.post(`/pms/properties/${property.id}/setup-complete`, skip ? { skip_mapping: true } : {});
      toast.success(skip ? "Mapping skipped — continue to billing" : "Setup complete — review your invoice");
      await reload();
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };

  const hasAnySelection = mappings.length > 0 || !!pendingListingId;
  const poolIsEmpty = hotelListings.length === 0;
  const showEmptyPoolPromo = poolIsEmpty && !hasAnySelection && !isPaid;

  // Show the connection chart once a listing has been picked. The chart is
  // the "Step 2.5" gate before billing so the host sees the full unit → room
  // map before the payment page.
  if (hasAnySelection && (showChart || isPaid)) {
    return (
      <HotelConnectionChart
        property={property}
        units={units}
        mappings={mappings}
        pool={pool}
        listingId={selectedListingId}
        busy={busy === "finish"}
        onBack={isPaid ? null : () => setShowChart(false)}
        onContinue={() => goToBilling(false)}
      />
    );
  }

  return (
    <div className="reveal">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Hotel size={17} /></span>
            Step 2 · pick your hotel listing
          </h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            You created <b>{units.length} unit{units.length === 1 ? "" : "s"}</b>. Pick the Atiti OTA listing that represents this hotel — each unit links 1:1 to a room from its inventory. It's OK if the OTA has more or fewer rooms than your PMS units; extras stay on OTA and surplus units run direct-only.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2 flex-wrap">
          {!isPaid && !showEmptyPoolPromo && (
            <button data-testid="hotel-mapping-skip-button" onClick={() => goToBilling(true)} disabled={busy === "finish"}
              className="text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-indigo-500 hover:text-indigo-600 flex items-center gap-1.5 disabled:opacity-50">
              {busy === "finish" ? <Loader2 className="animate-spin" size={13} /> : <SkipForward size={13} />} Skip for now
            </button>
          )}
          <button data-testid="hotel-mapping-refresh" onClick={refresh} disabled={refreshing}
            className="text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-700 flex items-center gap-1.5 disabled:opacity-50">
            {refreshing ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Refresh pool
          </button>
        </div>
      </div>

      {showEmptyPoolPromo ? (
        <EmptyPoolCTA pid={property.id} onRefresh={reload} onSkip={async () => { await reload(); onDone(); }} />
      ) : hotelListings.length === 0 ? (
        <div data-testid="no-hotel-listings" className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto">
            <AlertTriangle size={22} className="text-amber-500" />
          </div>
          <h3 className="font-display font-bold text-slate-900 mt-4">No hotel-type listings found</h3>
          <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
            Your Atiti account has no hotel/hostel/motel/dormitory listings yet. Create one on the Atiti OTA app with <b>{units.length} inventory</b>, then hit Refresh above.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hotel-listings-grid">
          {hotelListings.map((l) => {
            const inv = Number(l.total_inventory || 0);
            const isMapped = mappedListingId === l.listing_id;
            const isReserved = !isMapped && pendingListingId === l.listing_id;
            const isSelected = isMapped || isReserved;
            const isProcessing = busy === l.listing_id;
            // Variable inventory support — pick any listing regardless of
            // count. Chart on next screen shows exactly how each unit maps.
            const linkedCount = inv ? Math.min(inv, units.length) : units.length;
            const surplusUnits = Math.max(0, units.length - linkedCount);
            return (
              <button key={l.listing_id} data-testid={`hotel-listing-card-${l.listing_id}`}
                onClick={() => pickListing(l)}
                disabled={isProcessing || busy || isPaid}
                className={`text-left bg-white rounded-2xl border-2 p-5 transition-all ${
                  isMapped ? "border-emerald-500 ring-2 ring-emerald-200"
                          : isReserved ? "border-amber-400 ring-2 ring-amber-100"
                                       : "border-slate-200 hover:border-indigo-500 hover:shadow-md cursor-pointer"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-slate-900 text-sm truncate">{l.title}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{l.city} · {l.type || "Hotel"} · {l.bookings || 0} past bookings</p>
                  </div>
                  {isMapped && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1"><CheckCircle2 size={11} /> mapped</span>}
                  {isReserved && <span data-testid={`reserved-badge-${l.listing_id}`} className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5 flex items-center gap-1"><Sparkles size={11} /> reserved</span>}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                  <div>
                    <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">OTA inventory</p>
                    <p className="font-display font-bold text-lg text-slate-900">{inv || "—"} <span className="text-[10px] font-normal text-slate-500">rooms</span></p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      links <b>{linkedCount}</b>/{units.length} unit{units.length === 1 ? "" : "s"}
                      {surplusUnits > 0 ? ` · ${surplusUnits} direct-only` : ""}
                    </p>
                  </div>
                  {isProcessing ? (
                    <span className="text-[11px] font-semibold text-indigo-600 flex items-center gap-1.5"><Loader2 className="animate-spin" size={11} /> reserving…</span>
                  ) : isMapped ? (
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-semibold text-emerald-700">re-map</span>
                      <button data-testid={`unmap-listing-${l.listing_id}`}
                        onClick={async (e) => { e.stopPropagation();
                          try {
                            await api.post(`/pms/pool/${l.listing_id}/unmap`);
                            toast.success(`Unmapped "${l.title}" — units are free to remap`);
                            await reload();
                          } catch (err) { toast.error(errMsg(err)); }
                        }}
                        className="text-[10px] font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                        Unmap
                      </button>
                    </span>
                  ) : isReserved ? (
                    <span className="text-[11px] font-semibold text-amber-700">tap to change</span>
                  ) : (
                    <span className="text-[11px] font-semibold text-indigo-600 flex items-center gap-1.5"><Zap size={11} /> tap to reserve</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {(mappings.length > 0 || pendingListingId) && (
        <div data-testid="hotel-mapping-summary" className={`mt-6 border-2 rounded-2xl p-5 flex items-start gap-4 flex-wrap ${
          mappings.length > 0 ? "bg-gradient-to-br from-emerald-50 via-white to-white border-emerald-200"
                              : "bg-gradient-to-br from-amber-50 via-white to-white border-amber-200"}`}>
          <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
            mappings.length > 0 ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`}>
            <Sparkles size={19} />
          </span>
          <div className="flex-1 min-w-0">
            {mappings.length > 0 ? (
              <>
                <p className="font-display font-bold text-slate-900 text-sm">All {mappings.length} units linked · property active</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Each unit is 1:1 paired with an OTA room. Bookings on this listing land on the next available unit automatically.</p>
              </>
            ) : (
              <>
                <p className="font-display font-bold text-slate-900 text-sm">{units.length} units reserved · pay to lock this listing</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Your chosen listing is <b>reserved but not permanently linked</b> yet — that only happens after your first payment clears. Feel free to swap it before you pay.
                </p>
              </>
            )}
          </div>
          <button data-testid="hotel-mapping-finish-button" onClick={() => goToBilling(false)} disabled={busy === "finish"}
            className="btn-brand rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2 shrink-0">
            {busy === "finish" ? <Loader2 className="animate-spin" size={15} /> : null} Continue to billing <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
